import { useEffect, useRef, useState } from 'react';
import {
  AgnesPageHeader,
  AgnesBreadcrumb,
  AgnesCard,
  AgnesLabel,
  AgnesInput,
  AgnesButton,
} from '@agnes/ui';
import { useAuthStore } from '@/stores/useAuthStore';
import { fullName, initials } from '@/utils/userDisplay';
import { notify } from '@/stores/useToast';
import ServiceAuth from '@/services/ServiceAuth';
import { authStorage } from '@/lib/authStorage';

export default function PagePerfil() {
  const user = useAuthStore((s) => s.user);
  const getUser = useAuthStore((s) => s.getUser);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [telefone, setTelefone] = useState('');
  const [saving, setSaving] = useState(false);
  const seeded = useRef(false);

  useEffect(() => {
    if (!user.firstName) getUser();
  }, [user.firstName, getUser]);

  // Semeia o formulário UMA vez, quando os dados do usuário chegam. Não re-semear em
  // toda mudança de `user` evita sobrescrever o que o usuário já está digitando.
  useEffect(() => {
    if (seeded.current) return;
    if (!user.firstName && !user.lastName && !user.phone && !user.emailAddress) return;
    setFirstName(user.firstName ?? '');
    setLastName(user.lastName ?? '');
    setEmail(user.emailAddress ?? '');
    setTelefone(user.phone ?? '');
    seeded.current = true;
  }, [user]);

  async function handleSave() {
    if (!firstName.trim()) {
      notify.error('Informe o nome.');
      return;
    }
    if (!email.trim()) {
      notify.error('Informe o e-mail.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      notify.error('Informe um e-mail válido.');
      return;
    }
    setSaving(true);
    try {
      await ServiceAuth.user.update({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        emailAddress: email.trim(),
        phone: telefone,
      });
      await getUser();
      notify.success('Perfil atualizado.');
    } catch {
      notify.error('Não foi possível salvar as alterações.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="p-6">
      <AgnesBreadcrumb items={[{ label: 'Home', href: '/home' }, { label: 'Meu perfil' }]} />
      <AgnesPageHeader title="Meu perfil" subtitle="Dados da sua conta" />

      <AgnesCard className="mt-2 max-w-xl p-6">
        <div className="mb-6 flex items-center gap-4">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-agnes-primary text-xl font-semibold text-white">
            {initials(user)}
          </div>
          <div>
            <div className="text-lg font-semibold text-agnes-text-primary">
              {fullName(user) || '-'}
            </div>
            <div className="text-sm text-agnes-text-muted">{user.emailAddress ?? '-'}</div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <AgnesLabel>Nome</AgnesLabel>
            <AgnesInput value={firstName} onChange={(e) => setFirstName(e.target.value)} />
          </div>
          <div>
            <AgnesLabel>Sobrenome</AgnesLabel>
            <AgnesInput value={lastName} onChange={(e) => setLastName(e.target.value)} />
          </div>
          <div>
            <AgnesLabel>E-mail</AgnesLabel>
            <AgnesInput
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="nome@empresa.com"
            />
          </div>
          <div>
            <AgnesLabel>Organização</AgnesLabel>
            <AgnesInput value={authStorage.getItem('tenant_slug') ?? ''} disabled />
          </div>
          <div>
            <AgnesLabel>Telefone</AgnesLabel>
            <AgnesInput
              value={telefone}
              onChange={(e) => setTelefone(e.target.value)}
              placeholder="(00) 00000-0000"
            />
          </div>
        </div>

        <div className="mt-6 flex justify-end">
          <AgnesButton variant="primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Salvando...' : 'Salvar alterações'}
          </AgnesButton>
        </div>
      </AgnesCard>
    </div>
  );
}
